import {
  QueryActionCreatorResult,
  QueryDefinition,
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query';
import { ClassValue } from 'clsx';
import { useTranslation } from 'react-i18next';

import { LoadingSpinner } from '@app/shared/ui/atoms/Common';
import { NotFound } from '@app/shared/ui/molecules/Common';
import { cn } from '@app/shared/utils/common';
import { UsersTagType } from '@/app/store/slices/users/usersTypes';
import { BalanceHistoryListItem } from '@/app/shared/ui/molecules/BalanceHistory';
import { BalanceHistoryRow, BalanceHistoryRequest } from '@/app/store/slices/balance/balanceTypes';
import { PriceType } from '@/app/shared/types/common';

const classNameTable: ClassValue = ['w-full', 'bg-blue-soft'];
const classNameRow: ClassValue = ['h-20', 'text-left'];
const classNameFirstColumn: ClassValue = [
  'text-xs',
  'min-w-36',
  // 'w-[35px]',
  'border-[hsl(var(--background))]',
  'border-2',
  'border-solid',
  'border-l-0',
  'py-1',
  'px-1',
];
const classNameSecondColumn: ClassValue = [
  'text-xs',
  // 'whitespace-nowrap',
  // 'min-w-32',
  'w-[50px]',
  'border-[hsl(var(--background))]',
  'border-2',
  'border-solid',
  'border-l-0',
  'py-1',
  'px-1',
];
const classNameThirdColumn: ClassValue = [
  'text-xs',
  'whitespace-nowrap',
  'text-right',
  'word-wrap-none',
  'min-w-14',
  // 'w-[20px]',
  'border-[hsl(var(--background))]',
  'border-2',
  'border-solid',
  'border-l-0',
  'py-1',
  'px-1',
];
const classNameFourthColumn: ClassValue = [
  'text-xs',
  'whitespace-nowrap',
  'text-center',
  'min-w-30',
  'w-[30px]',
  'border-[hsl(var(--background))]',
  'border-2',
  'border-solid',
  'border-r-0',
  'py-1',
  'px-1',
];

interface BalanceHistoryListProps {
  rows?: BalanceHistoryRow[] | null;
  isBalanceHistoryLoading: boolean;
  className?: ClassValue;
  type?: string | null;
  handleBalanceTypeByChange: (value: PriceType | null) => void;
  refetch: () => QueryActionCreatorResult<
    QueryDefinition<
      BalanceHistoryRequest,
      BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
      UsersTagType,
      BalanceHistoryRow[] | null,
      'usersApi'
    >
  >;
}

export function BalanceHistoryList({
  rows,
  isBalanceHistoryLoading,
  refetch,
  type,
  handleBalanceTypeByChange,
  className = '',
}: BalanceHistoryListProps) {
  const { t } = useTranslation();

  return (
    <div className={cn(className, 'bg-background pb-20')}>
      <div className={cn('flex items-center w-full bg-background text-card-foreground py-2')}>
        <div className={cn('px-1')}>
          <span className={cn('text-xs justify-between align-middle pr-1')}>Выберите валюту:</span>
          <select
            className={cn('text-xs justify-between align-middle rounded-sm p-1 text-stone-950')}
            onChange={(e) => {
              handleBalanceTypeByChange(e.target.value as PriceType | null);
            }}
            defaultValue={type ?? '-'}
          >
            <option value="">-</option>
            <option value="ton">TON</option>
            <option value="cookie">Cookie</option>
          </select>
        </div>
        <div className={cn('ml-auto justify-between align-middle items-center')}>
          <button
            className={cn(
              'px-5 rounded-xl border-slate-500 border-2 text-xs justify-between align-middle items-center'
            )}
            onClick={refetch}
          >
            {/* <img src={} /> */}Обновить
          </button>
        </div>
      </div>

      {isBalanceHistoryLoading && <LoadingSpinner size="lg" />}

      {(!rows || rows.length === 0) && <NotFound />}

      {rows && rows.length > 0 && (
        <table className={cn(classNameTable)}>
          <thead className={cn('font-semibold bg-blue-primary')}>
            <tr className={cn(classNameRow, 'text-white h-8')}>
              <td className={cn(classNameFirstColumn, 'text-center')}>
                {t('balance.history.description')}
              </td>
              <td className={cn(classNameSecondColumn, 'text-center')}>
                {t('balance.history.source')}
              </td>
              <td className={cn(classNameThirdColumn, 'text-center')}>
                {t('balance.history.amount')}
              </td>
              <td className={cn(classNameFourthColumn, 'text-center')}>
                {t('balance.history.date')}
              </td>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <BalanceHistoryListItem
                key={row.id}
                {...row}
                classNameRow={classNameRow}
                classNameFirstColumn={classNameFirstColumn}
                classNameSecondColumn={classNameSecondColumn}
                classNameThirdColumn={classNameThirdColumn}
                classNameFourthColumn={classNameFourthColumn}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
